import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  getImportJob,
  promoteImportJob,
  promoteResultFromJob,
  startImportJob,
  type StartImportJobInput,
} from './api';
import type { ImportJobDto, ImportJobStatus, PromoteResult } from './types';

const KEY = 'import-job' as const;

const POLL_STATUSES: ReadonlySet<ImportJobStatus> = new Set<ImportJobStatus>([
  'pending',
  'running',
]);

export const importJobKey = (jobId: string) => [KEY, jobId] as const;

/**
 * Polls every 2s while the job is in `pending` or `running` state, then
 * settles into the terminal status. React-query handles the lifecycle.
 */
export const useImportJob = (jobId: string | undefined) => {
  return useQuery<ImportJobDto, ApiClientError>({
    queryKey: [KEY, jobId ?? ''],
    queryFn: () => getImportJob(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && POLL_STATUSES.has(data.status)) return 2000;
      return false;
    },
  });
};

export const useStartImportJob = () => {
  const qc = useQueryClient();
  return useMutation<ImportJobDto, ApiClientError, StartImportJobInput>({
    mutationFn: (input) => startImportJob(input),
    onSuccess: (job) => {
      qc.setQueryData([KEY, job.import_job_id], job);
    },
  });
};

export const usePromoteImportJob = (jobId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<PromoteResult, ApiClientError, void>({
    mutationFn: async () => {
      // P1-#8: backend returns the post-promote job DTO; we derive the
      // promote counts from `summary` for the UI.
      const job = await promoteImportJob(jobId as string);
      qc.setQueryData([KEY, job.import_job_id], job);
      return promoteResultFromJob(job);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, jobId ?? ''] });
      qc.invalidateQueries({ queryKey: ['staging-rows', jobId ?? ''] });
    },
  });
};
